<template>
  <div class="stepper">
    <Logo class="stepper__logo" :withText="true" />

    <p v-if="!hideSectionName" class="stepper__section-name">
      Step {{ currentVisibleIndex + 1 }} <strong>•</strong>
      {{ view.currentSection.title }}
    </p>

    <Transition name="slide" mode="out-in">
      <div :key="view.currentSectionIndex" class="stepper__steps">
        <div
          v-for="(section, index) in visibleSections"
          :key="section.id"
          class="stepper__step"
          :class="{
            'stepper__step--prev': index < currentVisibleIndex,
            'stepper__step--current': index === currentVisibleIndex,
            'stepper__step--next': index > currentVisibleIndex,
          }"
          :style="{
            '--section-progress': view.currentSectionProgress,
          }"
        ></div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { QuestionnaireView } from '@/models/onboarding'

import Logo from '@/components/ui/Logo.vue'

const props = defineProps<{
  view: QuestionnaireView
  hideSectionName?: boolean
}>()

// Sections flagged `excludeFromStepper` (e.g., the intro) never get a segment,
// regardless of their array position, this doesn't assume the excluded
// section is always index 0.
const visibleSections = computed(() =>
  props.view.sections.filter((section) => !section.excludeFromStepper),
)

const currentVisibleIndex = computed(() =>
  visibleSections.value.findIndex(
    (section) => section.id === props.view.currentSection.id,
  ),
)
</script>

<style lang="scss" scoped>
$step-min-width: 16px;

.stepper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-block: 22px 16px;
  padding-inline: var(--onboarding-padding);
  background: transparent;
  border-bottom: 1px solid transparent;
  @supports (padding-top: env(safe-area-inset-top)) {
    padding-top: max(22px, env(safe-area-inset-top));
  }
  @media (max-height: 650px) {
    padding-block: 22px 8px;
    @supports (padding-top: env(safe-area-inset-top)) {
      padding-top: max(22px, env(safe-area-inset-top));
    }
  }
  @media (min-width: $w-pc-b) {
    border: none;
    padding-bottom: 0;
  }

  &__logo {
    display: none;
    height: 24px;
    width: auto;
    margin-bottom: 16px;

    @media (min-width: $w-pc-b) {
      display: block;
    }
  }

  &__section-name {
    display: none;
    font-size: 14px;
    line-height: 1;
    color: $c-black-6;
    margin-bottom: 10px;
  }

  &__steps {
    display: flex;
    gap: 8px;
    width: 100%;
    max-width: var(--onboarding-inner-width);
    @media (min-width: $w-pc-b) {
      max-width: 600px;
    }
  }

  &__step {
    height: 6px;
    min-width: $step-min-width;
    border-radius: 6px;
    background: rgb(255 255 255 / 40%);
  }

  &__step--prev {
    position: relative;
    background: $c-white;
  }

  &__step--current {
    position: relative;
    width: 100%;

    &:after {
      content: '';
      display: block;
      width: calc(100% * var(--section-progress));
      height: 100%;
      border-radius: 3px;
      background-color: $c-white;
      transition: width 0.3s ease;
    }
  }

  .onboarding-questionnaire:has(
      .onboarding-pact__loading-screen--final,
      .onboarding-pact__card--back.onboarding-pact__card--visible
    )
    &__step--current:after {
    width: 100%;
  }
}

.slide {
  &-leave-active {
    transition: all 1s ease;
  }

  &-leave-active .stepper__step--current {
    animation: 1s slide-out ease forwards;
  }

  &-leave-active .stepper__step--current + .stepper__step--next {
    animation: 1s slide-in ease forwards;
  }

  &-leave-active .stepper__step--current:after {
    transition: all 0.3s;
  }

  &-leave-to .stepper__step--current:after {
    width: 100%;
  }
}

@keyframes slide-out {
  0%,
  50% {
    width: 100%;
  }

  100% {
    width: $step-min-width;
  }
}

@keyframes slide-in {
  0%,
  50% {
    width: $step-min-width;
  }

  100% {
    width: 100%;
  }
}
</style>
